import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { monthlyReportsService } from './service';
import type { MonthlyReportStatus } from './types';

const KEYS = {
  all: ['monthly-reports'] as const,
  vesselList: (vesselId: string) =>
    [...KEYS.all, 'vessel', vesselId] as const,
  adminList: (status?: MonthlyReportStatus) =>
    [...KEYS.all, 'admin', status ?? 'all'] as const,
};

export function useVesselMonthlyReports(vesselId: string | undefined) {
  return useQuery({
    queryKey: vesselId
      ? KEYS.vesselList(vesselId)
      : [...KEYS.all, 'idle'],
    queryFn: () => monthlyReportsService.listForVessel(vesselId!),
    enabled: Boolean(vesselId),
    // Reports are largely immutable once written — keep the cache
    // warm so opening the vessel page is instant.
    staleTime: 60_000,
  });
}

export function useAdminMonthlyReports(status?: MonthlyReportStatus) {
  return useQuery({
    queryKey: KEYS.adminList(status),
    queryFn: () => monthlyReportsService.adminList(status),
    staleTime: 30_000,
  });
}

export function useGenerateMonthlyReport(vesselId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { periodYear: number; periodMonth: number }) =>
      monthlyReportsService.generate(vesselId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

/**
 * Mutation that fetches a presigned URL and opens it in a new tab.
 * Kept as a mutation (not a query) so the click triggers it on
 * demand rather than running on mount — a presigned URL is a tiny
 * resource but we don't want to mint one for every report on the
 * page upfront.
 */
export function useDownloadMonthlyReport() {
  return useMutation({
    mutationFn: async (input: {
      vesselId: string;
      periodYear: number;
      periodMonth: number;
    }) => {
      const { url } = await monthlyReportsService.getPdfUrl(
        input.vesselId,
        input.periodYear,
        input.periodMonth,
      );
      window.open(url, '_blank', 'noopener,noreferrer');
      return url;
    },
  });
}
